import React from "react"
import Typography from "aldoo-ra/Typography"
import Badge from "aldoo-ra/Badge"
import Avatar from "aldoo-ra/Avatar"
import Tooltip from "aldoo-ra/Tooltip"
import moment from "moment"
import { ChevronRightIcon } from "@heroicons/react/24/outline"

/**
 * Get action badge variant based on action type
 */
const getActionVariant = (action) => {
  switch (action) {
    case 'create':
      return "admin_success"
    case 'update':
      return "admin_info"
    case 'delete':
      return "admin_error"
    case 'soft-delete':
      return "admin_warning"
    default:
      return "admin"
  }
}

/**
 * History Entry Card - Reusable component for displaying history entries
 */
const HistoryEntryCard = ({ entry, isSelected, onClick }) => {
  return (
    <div
      className={`bg-admin_paper border rounded-lg p-4 transition-colors cursor-pointer group overflow-hidden ${
        isSelected ? 'border-admin_primary' : 'border-admin_bordered hover:border-admin_primary'
      }`}
      onClick={onClick}
    >
      <div className="w-full flex items-center gap-3 min-w-0">
        {/* Avatar - fixed */}
        <div className="flex-shrink-0">
          <Avatar
            user={{ name: entry.author || "Unknown" }}
            variant="admin_medium"
          />
        </div>

        {/* Username */}
        <div className="w-35 xl:w-40 min-w-35 flex flex-col flex-shrink-0">
          <Typography 
            variant="admin_txt1" 
            className="font-semibold"
          >
            {entry.author || "Unknown"}
          </Typography>

          <Tooltip 
            display="block"
            content={moment(entry.timestamp).format('MMMM D, YYYY [at] h:mm:ss A')}
          >
            <Typography 
              variant="admin_txt2_dim" 
              className="truncate"
            >
              {moment(entry.timestamp).fromNow()}
            </Typography>
          </Tooltip>
        </div>

        {/* Action - fixed small */}
        <div className="w-22 xl:w-28 flex-shrink-0">
          <Badge 
            variant={getActionVariant(entry.action)} 
            className="capitalize text-sm whitespace-nowrap"
          >
            {entry.action}
          </Badge>
        </div>

        {/* Target Type - can shrink */}
        <div className="w-40 min-w-20 shrink">
          <Typography 
            variant="admin_txt1" 
            className="truncate font-medium"
          >
            {entry.targetModel}
          </Typography>
        </div>

        {/* Target Name - flexible */}
        <div className="w-40 min-w-10">
          <Typography 
            variant="admin_txt1" 
            className="truncate"
          >
            {entry.targetName || "-"}
          </Typography>
        </div>

        {/* Fields or Changelog Summary - can shrink */}
        <div className="w-40 min-w-20 shrink ml-auto">
          {/* Show changelog if available (for bulk operations) */}
          {entry.changelog ? (
            <div className="flex items-center gap-2 min-w-0 overflow-hidden">
              <Badge 
                variant="admin_default" 
                className="text-sm flex-shrink-0"
              >
                Bulk
              </Badge>

              <Typography 
                variant="admin_txt1" 
                className="text-admin_text_secondary italic truncate min-w-0"
              >
                {entry.changelog}
              </Typography>
            </div>
          ) : (
            // Show field changes for regular operations
            entry.changes && entry.changes.length > 0 && (
              <div className="flex gap-1 overflow-hidden justify-end min-w-0">
                {entry.changes.slice(0, 1).map((field, i) => (
                  <span key={i} 
                    className="px-2 py-1 bg-admin_canvas rounded text-sm font-mono text-admin_text_secondary truncate min-w-0"
                  >
                    {field}
                  </span>
                ))}

                {entry.changes.length > 1 && (
                  <span className="text-sm text-admin_text_secondary whitespace-nowrap bg-admin_neutral_1 rounded-full leading-none p-2 flex-shrink-0">
                    +{entry.changes.length - 1}
                  </span>
                )}
              </div>
            )
          )}
        </div>

        {/* Details Arrow - fixed */}
        <div className="flex-shrink-0">
          <ChevronRightIcon className="w-5 h-5 text-admin_text_secondary group-hover:text-admin_primary transition-colors" />
        </div>
      </div>
    </div>
  )
}

export default HistoryEntryCard